import styled from '@emotion/styled'
import { useCallback, useEffect, useRef, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { Link } from 'react-router'

import { ArtInvestmentData, CircleCheckFilled, WarningTriangle } from '../../../../assets/icons'
import { CHECK_GREEN } from '../../../../assets/icons/CircleCheckFilled'
import { Container } from '../../../../components'
import Button from '../../../../components/Button'
import { withRouterProps } from '../../../../utils'
import { UPLOAD_STATUS, investmentsReviewPath } from '../../../../models/InvestmentUpload'
import UploadSpinner from './UploadSpinner'
import { UPLOAD_COPY, validateStatementFiles } from './uploadCopy'

const Page = styled.div`
  position: relative;
  max-width: 640px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  text-align: center;
`

const CloseIcon = styled.button`
  position: absolute;
  top: 0;
  right: 8px;
  padding: 4px 10px;
  border: none;
  background: none;
  color: ${p => p.theme.lightestGray ?? '#7a8e96'};
  font-size: 34px;
  line-height: 24px;
  cursor: pointer;
`

const Heading = styled.h1`
  margin: 34px 0 0;
  color: ${p => p.theme.darkestGray ?? '#022a3a'};
  font-size: 30px;
  font-weight: 300;
  line-height: 40px;
`

const Body = styled.p`
  margin: 18px 0 0;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-size: 18px;
  line-height: 24px;
`

const Illustration = styled.div`
  display: flex;
  justify-content: center;
  margin: 30px 0;
`

const SectionTitle = styled.h2`
  margin: 30px 0 12px;
  color: ${p => p.theme.darkestGray ?? '#022a3a'};
  font-size: 16px;
  font-weight: 600;
  text-align: left;
`

const CheckList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  text-align: left;

  li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;
    color: ${p => p.theme.mediumGray ?? '#4a606a'};
    font-size: 18px;
    line-height: 24px;
  }

  svg {
    flex: 0 0 auto;
  }
`

const Steps = styled.ol`
  margin: 0;
  padding-left: 22px;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-size: 18px;
  line-height: 24px;
  text-align: left;

  li {
    margin-bottom: 8px;
  }
`

/** Bordered note card used by the processing state. */
const NoteCard = styled.div`
  box-sizing: border-box;
  margin: 20px 0;
  padding: 20px 10px;
  border: 2px solid ${p => p.theme.primaryLighter ?? '#d4ebf2'};
  border-radius: 10px;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-size: 18px;
  line-height: 24px;
`

const FileName = styled.div`
  margin: 20px 0 0;
  color: ${p => p.theme.darkestGray ?? '#022a3a'};
  font-size: 16px;
  font-weight: 600;
`

const StatusIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 34px;
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 16px;
  margin-top: 34px;

  > button {
    width: 100%;
  }
`

/* "Don't show me again" is off until the backend can persist the choice; without somewhere to
   store it the intro would reappear anyway, so offering it would be a lie.
   // TODO(GC-17972): restore when user-settings can store the choice.
const TextLink = styled.button`
  border: none;
  background: none;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-family: inherit;
  font-size: 16px;
  text-decoration: underline;
  cursor: pointer;
` */

const Disclaimer = styled.p`
  margin: 34px 0 0;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-size: 14px;
  line-height: 20px;

  a {
    color: ${p => p.theme.primaryColor ?? '#038ab7'};
    text-decoration: underline;
  }
`

const HiddenInput = styled.input`
  display: none;
`

/**
 * Mobile statement upload flow.
 *
 * Where desktop opens a modal, mobile navigates here. The intro explains the requirements and
 * hands off to the device's file picker; the picker overlays the page, so the "pick a file"
 * view behind it doubles as the fallback if the user dismisses the picker without choosing.
 */
const UploadStatementPage = props => {
  const { store } = props
  const { type, accountType, id: accountId } = props.match.params
  const inputRef = useRef(null)
  // Always starts on the intro. // TODO(GC-17972): restore when user-settings can store the choice.
  // const { userSettings: { hideUploadStatementIntro }, setHideUploadStatementIntro } = store
  const [showIntro, setShowIntro] = useState(true)
  const upload = store.getUpload(accountId)
  const status = upload?.status ?? UPLOAD_STATUS.idle
  const isUploading = store.isAccountUploading(accountId)
  const reviewPath = investmentsReviewPath({ type, accountType, accountId })
  const summaryPath = `/accounts/${type}/${accountType}/${accountId}`

  // Registered here for the same reason the desktop modal does it: while this page is on
  // screen the user drives what happens next, so the background completion must stand down.
  useEffect(() => {
    store.registerUploadView(accountId)

    return () => store.unregisterUploadView(accountId)
  }, [accountId, store])

  const openPicker = useCallback(() => {
    setShowIntro(false)
    inputRef.current?.click()
  }, [])

  /* Someone who has opted out of the intro goes straight to the picker.
     // TODO(GC-17972): restore when user-settings can store the choice.
  useEffect(() => {
    if (hideUploadStatementIntro && status === UPLOAD_STATUS.idle) openPicker()
    // Only on mount: re-running would reopen the picker after every state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) */

  const handleChange = event => {
    const files = Array.from(event.target.files ?? [])
    // Reset so re-picking the same file still fires a change event.
    event.target.value = ''

    const error = validateStatementFiles(files)

    if (error) {
      store.showToast({ severity: 'error', message: error })
      return
    }

    if (!files.length) return

    store.startStatementUpload({ type, accountType, accountId, file: files[0] })
  }

  /* // TODO(GC-17972): restore when user-settings can store the choice.
  const handleDontShowAgain = () => {
    setHideUploadStatementIntro(true)
    openPicker()
  } */

  /** Leaves the flow. An upload already in flight keeps running and finishes with a toast. */
  const handleClose = () => {
    if (!isUploading) store.clearUpload(accountId)
    props.navigate(summaryPath)
  }

  const handleReview = async () => {
    store.setInvestmentsReview(accountId, true, reviewPath)
    await store.refreshAccountAfterUpload(accountId)
    store.clearUpload(accountId)
    props.navigate(reviewPath)
  }

  const handleRetry = () => {
    store.clearUpload(accountId)
    openPicker()
  }

  /**
   * Leaving on success has to hand the document over, not just dismiss.
   *
   * A completion that lands while this page is open deliberately skips the refresh, the review and
   * the toast, because the page is expected to own the handoff. Dismissing through the generic
   * close would drop all three, leaving the account stale and the extracted investments unreviewed.
   */
  const dismiss = status === UPLOAD_STATUS.success ? handleReview : handleClose

  const disclaimer = (
    <Disclaimer>
      {UPLOAD_COPY.disclaimer} <Link to='/ai-disclaimer'>{UPLOAD_COPY.disclaimerLink}</Link>
    </Disclaimer>
  )

  const requirements = (
    <>
      <SectionTitle>{UPLOAD_COPY.fileRequirementsTitle}</SectionTitle>
      <CheckList>
        {UPLOAD_COPY.fileRequirements.map(requirement => (
          <li key={requirement}>
            <CircleCheckFilled checkColor={CHECK_GREEN} />
            {requirement}
          </li>
        ))}
      </CheckList>
    </>
  )

  const renderContent = () => {
    if (isUploading) {
      return (
        <>
          <Heading>{UPLOAD_COPY.processingHeading}</Heading>
          <NoteCard>{UPLOAD_COPY.processingLead}</NoteCard>
          <StatusIcon>
            <UploadSpinner />
          </StatusIcon>
          <NoteCard>{UPLOAD_COPY.processingNote}</NoteCard>
        </>
      )
    }

    if (status === UPLOAD_STATUS.success) {
      return (
        <>
          <Heading>{UPLOAD_COPY.successHeading}</Heading>
          <StatusIcon>
            <CircleCheckFilled width='50' height='50' checkColor={CHECK_GREEN} />
          </StatusIcon>
          <FileName>{upload?.fileName}</FileName>
          <Body>{UPLOAD_COPY.successBody}</Body>
          <Actions>
            <Button variant='primary' type='button' onClick={handleReview}>
              Review
            </Button>
          </Actions>
        </>
      )
    }

    if (status === UPLOAD_STATUS.error || status === UPLOAD_STATUS.backendError) {
      const isBackendError = status === UPLOAD_STATUS.backendError

      return (
        <>
          <Heading>
            {isBackendError ? UPLOAD_COPY.backendErrorHeading : UPLOAD_COPY.errorHeading}
          </Heading>
          <StatusIcon>
            <WarningTriangle />
          </StatusIcon>
          <FileName>{upload?.fileName}</FileName>
          <Body>{isBackendError ? UPLOAD_COPY.backendErrorBody : UPLOAD_COPY.errorBody}</Body>
          <Actions>
            {isBackendError ? (
              // Retrying the same request will not help, so the only way out is to leave.
              <Button variant='secondary' type='button' onClick={handleClose}>
                Close
              </Button>
            ) : (
              <Button variant='tertiary' type='button' onClick={handleRetry}>
                Pick a Different File
              </Button>
            )}
          </Actions>
        </>
      )
    }

    return (
      <>
        <Heading>Upload your statement</Heading>
        <Body>{UPLOAD_COPY.intro}</Body>
        <Illustration>
          <ArtInvestmentData />
        </Illustration>
        {requirements}
        {showIntro && (
          <>
            <SectionTitle>{UPLOAD_COPY.findStatementTitle}</SectionTitle>
            <Steps>
              {UPLOAD_COPY.findStatementSteps.map(step => (
                <li key={step}>{step}</li>
              ))}
            </Steps>
          </>
        )}
        <Actions>
          <Button variant='primary' type='button' onClick={openPicker}>
            {showIntro ? 'Got it' : 'Pick a file'}
          </Button>
          {/* // TODO(GC-17972): restore when user-settings can store the choice.
          {showIntro && (
            <TextLink type='button' onClick={handleDontShowAgain}>
              Don’t show me again
            </TextLink>
          )} */}
        </Actions>
        {disclaimer}
      </>
    )
  }

  return (
    <Container>
      <Page>
        <CloseIcon type='button' aria-label='Close' onClick={dismiss}>
          &times;
        </CloseIcon>
        {renderContent()}
        <HiddenInput
          ref={inputRef}
          type='file'
          accept='application/pdf,.pdf'
          onChange={handleChange}
          data-testid='statement-file-input'
        />
      </Page>
    </Container>
  )
}

export default withRouterProps(inject('store')(observer(UploadStatementPage)))
